import React from 'react';
import {Avatar, Tooltip} from 'antd';
import {DEFAULT_PAGE_SIZE} from '../../constants';
import {textHelpers} from './text.helpers';

export const tableHelpers = {
  getOrderByValue: (sorter = {}) => {
    const {order, field} = sorter;
    return order ? `${order === 'descend' ? '-' : ''}${field}` : undefined
  },
  handleOnPaginate: ({activePage = 1, loadData, setTableData, tableData = {}}) => {
    const { page: currentPage, loadedPages, pagination } = tableData;

    const isForwardPagination = activePage > currentPage;
    const isPageNotLoaded = loadedPages < activePage;

    if (isForwardPagination && isPageNotLoaded) {
      const query = pagination?.cursor ? { cursor: pagination.cursor } : {};
      loadData?.({ query });
      return;
    }

    setTableData?.({
      ...tableData,
      page: activePage,
    });
  },
  getTablePagination: ({
    handleOnPaginate,
    tableData = {},
    perPagePagination = DEFAULT_PAGE_SIZE
  } = {}) => {
    const {
      page = 1,
      pagination,
      loadedPages = 1,
      rows = []
    } = tableData;

    const hasCursorPagination = Boolean(pagination?.cursor);
    const totalRows = hasCursorPagination
      ? loadedPages * perPagePagination + 1
      : rows.length;

    return {
      current: page,
      disabled: rows.length === 0,
      onChange: handleOnPaginate,
      pageSize: perPagePagination,
      showSizeChanger: false,
      total: totalRows
    };
  },
  getLoadMoreProps: ({tableData, view, loadData} = {}) => {
    const cursor = tableData[view]?.pagination?.cursor || tableData?.pagination?.cursor;
    return {
      cursor: cursor,
      isEnabled: Boolean(cursor),
      onClick: () => loadData({view, query: { cursor }})
    }
  },
  formatFilterPayload: (key, value, options = {}) => {
    const dateFormat = options.dateFormat || 'YYYY-MM-DD';
    if (key === 'date_range') {
      const start = value?.[0] ?? null;
      const end = value?.[1] ?? null;
      const formattedStart = start ? start.format(dateFormat) : null;
      const formattedEnd = end ? end.format(dateFormat) : null;

      const dateKeys = {
        start: options.dateKeys?.start || 'start_date',
        end: options.dateKeys?.end || 'end_date',
        gte: options.dateKeys?.gte || 'date_gte',
        lte: options.dateKeys?.lte || 'date_lte'
      };

      return {
        updatedValues: { [dateKeys.start]: start, [dateKeys.end]: end },
        formattedValues: { [dateKeys.gte]: formattedStart, [dateKeys.lte]: formattedEnd }
      };
    }

    return {
      updatedValues: { [key]: value },
      formattedValues: { [key]: value }
    };
  },
  formatFilterQuery: ({filter = {}, data = {}, exportAdditionalFields = {}, dateKeys = { gte: 'start_date', lte: 'end_date' } } = {}) => {
    const {date_range: dateRange} = data;
    const exportTimeFormat = 'YYYY-MM-DD';

    const isValidDateRange = dateRange && dateRange.length === 2;

    if (!isValidDateRange) return;

    const startDate = dateRange[0].format(exportTimeFormat);
    const endDate = dateRange[1].format(exportTimeFormat);

    const filterFields = Object.fromEntries(
      Object.entries(filter).filter(([_, value]) => value !== undefined && value !== null && value !== '')
    );

    const tableAdditionalFields = Object.fromEntries(
      Object.entries(exportAdditionalFields).filter(([key]) => !(key in filterFields))
    );

    const query = {
      [dateKeys.gte]: startDate,
      [dateKeys.lte]: endDate,
      ...tableAdditionalFields,
      ...filterFields,
    };

    // if exportAdditionalFields has key and filterFields has the same key but empty or [], use the value from exportAdditionalFields
    Object.entries(exportAdditionalFields).forEach(([key, value]) => {
      if (key in filterFields) {
        const filterValue = filterFields[key];
        const isEmptyArray = Array.isArray(filterValue) && filterValue.length === 0;
        const isEmptyString = typeof filterValue === 'string' && filterValue.trim() === '';
        if (isEmptyArray || isEmptyString) {
          query[key] = value;
        }
      }
    });
    return query;
  },
  // formatFilterQuery: ({filter = {}, data = {}, exportAdditionalFields = {}, dateKeys = { start: 'start_date', end: 'end_date', gte: 'date_gte', lte: 'date_lte' } } = {}) => {
  //   const {date_range: dateRange} = data;
  //   const exportTimeFormat = 'YYYY-MM-DD';
  //
  //   const isValidDateRange = dateRange && dateRange.length === 2;
  //
  //   if (!isValidDateRange) return;
  //
  //   const startDate = dateRange[0].format(exportTimeFormat);
  //   const endDate = dateRange[1].format(exportTimeFormat);
  //
  //   const filterFields = Object.fromEntries(
  //     Object.entries(filter).filter(([_, value]) => value !== undefined && value !== null && value !== '')
  //   );
  //
  //   const tableAdditionalFields = Object.fromEntries(
  //     Object.entries(exportAdditionalFields).filter(([key]) => !(key in filterFields))
  //   );
  //
  //   const query = {
  //     [dateKeys.start]: startDate,
  //     [dateKeys.end]: endDate,
  //     ...tableAdditionalFields,
  //     ...filterFields,
  //   };
  getExportFileName: ({baseName, data} = {}) => {
    const dateRange = data?.date_range;
    if (!dateRange || dateRange.length !== 2) return baseName || 'export';
    const exportTimeFormat = 'YYYY-MM-DD';

    const startDate = dateRange[0].format(exportTimeFormat);
    const endDate = dateRange[1].format(exportTimeFormat);

    return baseName ? `${baseName}-${startDate}-${endDate}` : `${startDate}-${endDate}`;
  },
  getUserAvatar: ({ user, key, ...rest }) => {
    if (!user) return null;
    const name = user?.full_name || user?.email;
    return (
      <div key={key} {...rest}>
        <Tooltip title={name}>
          <Avatar src={user?.logo} size={24} style={{fontSize: 12}}>
            {textHelpers.getInitials(name)}
          </Avatar>
        </Tooltip>
      </div>
    );
  }
}
